import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  registerStart,
  registerSuccess,
  registerFailure,
  updateUser,
} from "../../store/authSlice";
import { showToast } from "../../store/uiSlice";
import api from "../../services/api";

const QUESTIONS = [
  {
    page: "1/6",
    question: "Main sources of funds available for trading",
    type: "multiple_choice",
    options: [
      "Employment / Business Income",
      "Savings and Investments",
      "Retirement Income",
      "Company Profit/Dividends",
      "Inheritance",
      "Property Sale",
      "Loan/ Borrowings/Gifts",
      "Other",
    ],
  },
  {
    page: "2/6",
    question:
      "What is the amount that you expect to deposit in the next 12 months? (USD)",
    type: "multiple_choice",
    options: [
      "0 - 20,000",
      "20,001 - 50,000",
      "50,001 - 250,000",
      "250,001 - 500,000",
      "500,001 - 1,000,000",
      "More than 1,000,000",
    ],
  },
  {
    page: "3/6",
    question: "Your total Annual Estimated Income (USD)",
    type: "multiple_choice",
    options: [
      "Less than 50,000",
      "50,001 - 100,000",
      "100,001 - 500,000",
      "500,001 - 1,000,000",
      "More than 1,000,000",
    ],
  },
  {
    page: "4/6",
    question:
      'Are you a Politically Exposed Person ("PEP") or a Family Member of a Close Associate of a PEP?',
    type: "yes_no",
    options: ["No", "Yes"],
  },
  {
    page: "5/6",
    question:
      "What is the nature of your profession/business or relevant former profession/business?",
    type: "multiple_choice",
    options: [
      "Accounting and Finance Services",
      "Company Service Providers",
      "Real Estate",
      "Pharmaceuticals and Healthcare",
      "Arms Trade and Defence",
      "Casinos",
      "Banking",
      "Financial Services",
      "Computer Software, Internet, IT",
      "Government Activities",
      "Mathematics",
      "Education",
      "Engineering and/or Construction",
      "Other",
    ],
  },
  {
    page: "6/6",
    question:
      "Do you understand that trading on CFDs carries significant risk of loss?",
    type: "confirmation",
    options: [
      "Yes, I confirm I understand that trading in CFDs carries significant risk of loss.",
    ],
  },
];

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [focusedField, setFocusedField] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [address, setAddress] = useState("");

  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Post-registration states
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showKyc, setShowKyc] = useState(false);
  const [questionStep, setQuestionStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmittingQuestionnaire, setIsSubmittingQuestionnaire] =
    useState(false);

  // KYC states
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmittingKyc, setIsSubmittingKyc] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    id: "not_uploaded",
    address: "not_uploaded",
    bank: "not_uploaded",
    selfie: "not_uploaded",
  });

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass) && /[a-z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^a-zA-Z0-9]/.test(pass)) strength++;
    setPasswordStrength(strength);
    return strength;
  };

  const validateStep1 = () => {
    if (!firstName || firstName.length < 2) {
      dispatch(
        showToast({
          message: "First name must be at least 2 characters.",
          type: "error",
        }),
      );
      return false;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(firstName)) {
      dispatch(
        showToast({ message: "First name: letters only.", type: "error" }),
      );
      return false;
    }
    if (!lastName || lastName.length < 2) {
      dispatch(
        showToast({
          message: "Last name must be at least 2 characters.",
          type: "error",
        }),
      );
      return false;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(lastName)) {
      dispatch(
        showToast({ message: "Last name: letters only.", type: "error" }),
      );
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      dispatch(
        showToast({
          message: "Please enter a valid email address.",
          type: "error",
        }),
      );
      return false;
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) {
      dispatch(
        showToast({
          message: "Phone number must be 7-15 digits.",
          type: "error",
        }),
      );
      return false;
    }
    if (!dateOfBirth) {
      dispatch(
        showToast({
          message: "Please select your date of birth.",
          type: "error",
        }),
      );
      return false;
    }
    if (!address || address.length < 5) {
      dispatch(
        showToast({
          message: "Address must be at least 5 characters.",
          type: "error",
        }),
      );
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!country) {
      dispatch(
        showToast({ message: "Please select your country.", type: "error" }),
      );
      return false;
    }
    if (!city || city.length < 2) {
      dispatch(
        showToast({
          message: "City name must be at least 2 characters.",
          type: "error",
        }),
      );
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (password.length < 8 || password.length > 16) {
      dispatch(
        showToast({
          message: "Password must be 8–16 characters.",
          type: "error",
        }),
      );
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      dispatch(
        showToast({
          message: "Password needs at least one uppercase letter.",
          type: "error",
        }),
      );
      return false;
    }
    if (!/[a-z]/.test(password)) {
      dispatch(
        showToast({
          message: "Password needs at least one lowercase letter.",
          type: "error",
        }),
      );
      return false;
    }
    if (!/[0-9]/.test(password)) {
      dispatch(
        showToast({
          message: "Password needs at least one number.",
          type: "error",
        }),
      );
      return false;
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      dispatch(
        showToast({
          message: "Password needs at least one special character.",
          type: "error",
        }),
      );
      return false;
    }
    if (password !== confirmPassword) {
      dispatch(
        showToast({ message: "Passwords do not match.", type: "error" }),
      );
      return false;
    }
    if (!agreeTerms) {
      dispatch(
        showToast({
          message: "Please agree to the Terms of Service.",
          type: "error",
        }),
      );
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    if (step === 2 && validateStep2()) setStep(3);
  };
  const prevStep = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;
    dispatch(registerStart());
    try {
      const response = await api.post("/auth/register", {
        firstName,
        lastName,
        email,
        phone,
        password,
        dateOfBirth,
        address,
        country,
        city,
      });
      dispatch(
        registerSuccess({
          user: response.data.user,
          token: response.data.token,
        }),
      );
      dispatch(
        showToast({
          message: "Account created successfully!",
          type: "success",
        }),
      );
      setShowQuestionnaire(true);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      dispatch(registerFailure(message));
      dispatch(showToast({ message, type: "error" }));
    }
  };

  // ─── Questionnaire handlers ───
  const handleQuestionnaireSkip = () => {
    setShowQuestionnaire(false);
    setShowKyc(true);
  };

  const handleQuestionnaireComplete = () => {
    setShowQuestionnaire(false);
    setShowKyc(true);
  };

  const handleQuestionnaireNext = async () => {
    const currentQuestion = QUESTIONS[questionStep];
    const selectedAnswer = answers[currentQuestion?.page] || "";
    if (!selectedAnswer) {
      dispatch(
        showToast({ message: "Please select an answer", type: "error" }),
      );
      return;
    }
    if (questionStep === QUESTIONS.length - 1) {
      setIsSubmittingQuestionnaire(true);
      try {
        const response = await api.post("/user/questionnaire", { answers });
        if (response.data.success) {
          dispatch(
            updateUser({
              questionnaire: answers,
              questionnaireComplete: response.data.questionnaireComplete,
            }),
          );
          dispatch(
            showToast({ message: "Questionnaire completed!", type: "success" }),
          );
        }
      } catch (error) {
        console.error("Failed to save questionnaire:", error);
      } finally {
        setIsSubmittingQuestionnaire(false);
        handleQuestionnaireComplete();
      }
    } else {
      setQuestionStep((prev) => prev + 1);
    }
  };

  // ─── KYC handlers ───
  const handleKycSkip = () => {
    setShowKyc(false);
    navigate("/terminal");
  };

  const handleKycFileUpload = async (type, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("type", type);
    formData.append("document", file);
    setIsUploading(true);
    try {
      await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadStatus((prev) => ({ ...prev, [type]: "uploaded" }));
      dispatch(showToast({ message: "Document uploaded!", type: "success" }));
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message || "Upload failed",
          type: "error",
        }),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleKycSubmit = async () => {
    setIsSubmittingKyc(true);
    try {
      await api.post("/documents/submit");
      dispatch(
        showToast({
          message: "Documents submitted for verification!",
          type: "success",
        }),
      );
    } catch (error) {
      console.error("Failed to submit documents:", error);
    } finally {
      setIsSubmittingKyc(false);
      setShowKyc(false);
      navigate("/terminal");
    }
  };

  const strengthLabels = ["Too weak", "Fair", "Good", "Strong"];
  const strengthColors = ["#ef4444", "#f59e0b", "#a834cb", "#10b981"];

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    )
      age--;
    return age;
  };

  const currentQuestion = QUESTIONS[questionStep];
  const selectedAnswer = answers[currentQuestion?.page] || "";
  const questionnaireProgress = ((questionStep + 1) / QUESTIONS.length) * 100;

  const kycDocuments = [
    {
      type: "id",
      name: "Government ID",
      required: true,
      icon: "fa-passport",
      color: "#a834cb",
      bg: "rgba(168, 52, 203, 0.12)",
      desc: "Passport, Driver's License, or National ID",
    },
    {
      type: "address",
      name: "Proof of Address",
      required: true,
      icon: "fa-file-alt",
      color: "#2563eb",
      bg: "rgba(37, 99, 235, 0.12)",
      desc: "Utility bill or bank statement",
    },
    {
      type: "bank",
      name: "Bank Statement",
      required: false,
      icon: "fa-bank",
      color: "#059669",
      bg: "rgba(5, 150, 105, 0.12)",
      desc: "Recent bank statement (optional)",
    },
    {
      type: "selfie",
      name: "Selfie with ID",
      required: true,
      icon: "fa-camera",
      color: "#d97706",
      bg: "rgba(217, 119, 6, 0.12)",
      desc: "Selfie holding your ID",
    },
  ];

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-3 xs:p-4 sm:p-6">
      {/* Header Navigation */}
      <div className="h-[5rem] absolute top-0 left-0 right-0 flex items-center justify-between px-3 xs:px-6 sm:px-8 py-3 xs:py-0 min-h-[70px] xs:min-h-[80px]">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img
            src="/src/assets/img/logo/logo-white.svg"
            alt="Anzo Trade"
            className="object-contain"
            style={{
              width: "clamp(120px, 10vw, 150px)",
              height: "clamp(140px, 7.5vw, 112px)",
            }}
          />
        </Link>
        <Link
          to="/login"
          className="text-[0.65rem] xs:text-xs sm:text-sm tracking-wider font-bold text-[#64748b] hover:text-[#a834cb] transition-colors uppercase whitespace-nowrap flex-shrink-0 ml-4"
        >
          Sign In
        </Link>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-md mt-[90px] xs:mt-24 sm:mt-28 md:mt-32">
        <div className="mb-8 xs:mb-9 sm:mb-10">
          <h1 className="text-3xl xs:text-4xl sm:text-5xl font-bold text-[#0f172a] mb-2 tracking-tight">
            Create Account
          </h1>
          <p className="text-[0.7rem] xs:text-xs sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase">
            Join thousands of traders
          </p>
        </div>

        {/* Step Indicators */}
        <div className="flex items-center gap-1.5 xs:gap-2 mb-8">
          {["Personal", "Location", "Security"].map((label, i) => {
            const s = i + 1;
            const isActive = step === s;
            const isDone = step > s;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5 xs:gap-2">
                  <div
                    className={`w-6 xs:w-7 h-6 xs:h-7 rounded-full flex items-center justify-center text-[10px] xs:text-xs font-bold transition-all ${isDone ? "bg-[#10b981] text-white" : isActive ? "bg-gradient-to-br from-[#a834cb] to-[#8b5cf6] text-white" : "bg-[#f1f5f9] text-[#94a3b8]"}`}
                  >
                    {isDone ? <i className="fas fa-check text-[9px]" /> : s}
                  </div>
                  <span
                    className={`text-[10px] xs:text-[11px] sm:text-xs tracking-wider font-bold uppercase transition-colors hidden xs:inline ${isActive ? "text-[#a834cb]" : isDone ? "text-[#10b981]" : "text-[#94a3b8]"}`}
                  >
                    {label}
                  </span>
                </div>
                {i < 2 && (
                  <div
                    className={`flex-1 h-0.5 ${step > s ? "bg-[#10b981]" : "bg-[#e2e8f0]"}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="space-y-5 xs:space-y-6 sm:space-y-6"
        >
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-5 xs:space-y-6">
              <div className="grid grid-cols-2 gap-3 xs:gap-4">
                {[
                  {
                    label: "First Name",
                    value: firstName,
                    setter: setFirstName,
                    placeholder: "John",
                    max: 10,
                  },
                  {
                    label: "Last Name",
                    value: lastName,
                    setter: setLastName,
                    placeholder: "Smith",
                    max: 11,
                  },
                ].map((f) => (
                  <div key={f.label}>
                    <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                      {f.label}
                    </label>
                    <input
                      type="text"
                      value={f.value}
                      onChange={(e) => f.setter(e.target.value)}
                      maxLength={f.max}
                      onFocus={() => setFocusedField(f.label)}
                      onBlur={() => setFocusedField(null)}
                      placeholder={f.placeholder}
                      className={`w-full px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] outline-none transition-all placeholder:text-[#cbd5e1] ${focusedField === f.label ? "border-b-[#a834cb]" : "border-b-[#cbd5e1] hover:border-b-[#c78ee8]"}`}
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] hover:border-b-[#c78ee8] transition-all placeholder:text-[#cbd5e1]"
                />
              </div>
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Phone
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-[#a834cb]">+</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) =>
                      setPhone(e.target.value.replace(/[^\d\s+\-()]/g, ""))
                    }
                    placeholder="1 555 000 0000"
                    className="flex-1 px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] transition-all placeholder:text-[#cbd5e1]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] transition-all"
                />
                {dateOfBirth && (
                  <p className="text-[10px] text-[#64748b] mt-1 font-medium">
                    Age: {calculateAge(dateOfBirth)} years
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Address
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main Street, Apt 4B"
                  className="w-full px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] hover:border-b-[#c78ee8] transition-all placeholder:text-[#cbd5e1]"
                />
              </div>
              <button
                type="button"
                onClick={nextStep}
                className="w-full py-3.5 sm:py-4 font-bold text-xs sm:text-base uppercase tracking-wider text-white rounded-md hover:shadow-xl active:scale-[0.98] transition-all border-b-[3px] border-[#8b2db5]"
                style={{
                  background:
                    "linear-gradient(to right, #a834cb, #8b5cf6, #6366f1)",
                }}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <div className="space-y-5 xs:space-y-6">
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Country
                </label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] transition-all"
                >
                  <option value="">Select your country</option>
                  <option>Afghanistan</option>
                  <option>Albania</option>
                  <option>Algeria</option>
                  <option>Andorra</option>
                  <option>Angola</option>
                  <option>Antigua and Barbuda</option>
                  <option>Argentina</option>
                  <option>Armenia</option>
                  <option>Austria</option>
                  <option>Azerbaijan</option>
                  <option>Bahamas</option>
                  <option>Bahrain</option>
                  <option>Bangladesh</option>
                  <option>Barbados</option>
                  <option>Belarus</option>
                  <option>Belgium</option>
                  <option>Belize</option>
                  <option>Benin</option>
                  <option>Bhutan</option>
                  <option>Bolivia</option>
                  <option>Bosnia and Herzegovina</option>
                  <option>Botswana</option>
                  <option>Brazil</option>
                  <option>Brunei</option>
                  <option>Bulgaria</option>
                  <option>Burkina Faso</option>
                  <option>Burundi</option>
                  <option>Cabo Verde</option>
                  <option>Cambodia</option>
                  <option>Cameroon</option>
                  <option>Central African Republic</option>
                  <option>Chad</option>
                  <option>Chile</option>
                  <option>China</option>
                  <option>Colombia</option>
                  <option>Comoros</option>
                  <option>Congo</option>
                  <option>Costa Rica</option>
                  <option>Côte d'Ivoire</option>
                  <option>Croatia</option>
                  <option>Cuba</option>
                  <option>Cyprus</option>
                  <option>Czech Republic</option>
                  <option>Democratic Republic of the Congo</option>
                  <option>Denmark</option>
                  <option>Djibouti</option>
                  <option>Dominica</option>
                  <option>Dominican Republic</option>
                  <option>Ecuador</option>
                  <option>Egypt</option>
                  <option>El Salvador</option>
                  <option>Equatorial Guinea</option>
                  <option>Eritrea</option>
                  <option>Estonia</option>
                  <option>Eswatini</option>
                  <option>Ethiopia</option>
                  <option>Fiji</option>
                  <option>Finland</option>
                  <option>France</option>
                  <option>Gabon</option>
                  <option>Gambia</option>
                  <option>Georgia</option>
                  <option>Ghana</option>
                  <option>Greece</option>
                  <option>Grenada</option>
                  <option>Guatemala</option>
                  <option>Guinea</option>
                  <option>Guinea-Bissau</option>
                  <option>Guyana</option>
                  <option>Haiti</option>
                  <option>Honduras</option>
                  <option>Hungary</option>
                  <option>Iceland</option>
                  <option>India</option>
                  <option>Indonesia</option>
                  <option>Iran</option>
                  <option>Iraq</option>
                  <option>Ireland</option>
                  <option>Italy</option>
                  <option>Jamaica</option>
                  <option>Japan</option>
                  <option>Jordan</option>
                  <option>Kazakhstan</option>
                  <option>Kenya</option>
                  <option>Kiribati</option>
                  <option>Kuwait</option>
                  <option>Kyrgyzstan</option>
                  <option>Laos</option>
                  <option>Latvia</option>
                  <option>Lebanon</option>
                  <option>Lesotho</option>
                  <option>Liberia</option>
                  <option>Libya</option>
                  <option>Liechtenstein</option>
                  <option>Lithuania</option>
                  <option>Luxembourg</option>
                  <option>Madagascar</option>
                  <option>Malawi</option>
                  <option>Malaysia</option>
                  <option>Maldives</option>
                  <option>Mali</option>
                  <option>Malta</option>
                  <option>Marshall Islands</option>
                  <option>Mauritania</option>
                  <option>Mauritius</option>
                  <option>Mexico</option>
                  <option>Micronesia</option>
                  <option>Moldova</option>
                  <option>Monaco</option>
                  <option>Mongolia</option>
                  <option>Montenegro</option>
                  <option>Morocco</option>
                  <option>Mozambique</option>
                  <option>Myanmar</option>
                  <option>Namibia</option>
                  <option>Nauru</option>
                  <option>Nepal</option>
                  <option>Netherlands</option>
                  <option>New Zealand</option>
                  <option>Nicaragua</option>
                  <option>Niger</option>
                  <option>Nigeria</option>
                  <option>North Korea</option>
                  <option>North Macedonia</option>
                  <option>Norway</option>
                  <option>Oman</option>
                  <option>Palau</option>
                  <option>Panama</option>
                  <option>Papua New Guinea</option>
                  <option>Paraguay</option>
                  <option>Peru</option>
                  <option>Philippines</option>
                  <option>Poland</option>
                  <option>Portugal</option>
                  <option>Qatar</option>
                  <option>Romania</option>
                  <option>Russia</option>
                  <option>Rwanda</option>
                  <option>Saint Kitts and Nevis</option>
                  <option>Saint Lucia</option>
                  <option>Saint Vincent and the Grenadines</option>
                  <option>Samoa</option>
                  <option>San Marino</option>
                  <option>São Tomé and Príncipe</option>
                  <option>Saudi Arabia</option>
                  <option>Senegal</option>
                  <option>Serbia</option>
                  <option>Seychelles</option>
                  <option>Sierra Leone</option>
                  <option>Singapore</option>
                  <option>Slovakia</option>
                  <option>Slovenia</option>
                  <option>Solomon Islands</option>
                  <option>Somalia</option>
                  <option>South Africa</option>
                  <option>South Korea</option>
                  <option>South Sudan</option>
                  <option>Spain</option>
                  <option>Sudan</option>
                  <option>Suriname</option>
                  <option>Sweden</option>
                  <option>Switzerland</option>
                  <option>Syria</option>
                  <option>Taiwan</option>
                  <option>Tajikistan</option>
                  <option>Tanzania</option>
                  <option>Thailand</option>
                  <option>Timor-Leste</option>
                  <option>Togo</option>
                  <option>Tonga</option>
                  <option>Trinidad and Tobago</option>
                  <option>Tunisia</option>
                  <option>Turkmenistan</option>
                  <option>Tuvalu</option>
                  <option>Uganda</option>
                  <option>Ukraine</option>
                  <option>United Arab Emirates</option>
                  <option>United Kingdom</option>
                  <option>Uruguay</option>
                  <option>Uzbekistan</option>
                  <option>Vanuatu</option>
                  <option>Vatican City</option>
                  <option>Venezuela</option>
                  <option>Vietnam</option>
                  <option>Yemen</option>
                  <option>Zambia</option>
                  <option>Zimbabwe</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) =>
                    setCity(e.target.value.replace(/[^a-zA-Z\s'-]/g, ""))
                  }
                  placeholder="Your city"
                  className="w-full px-3 py-2.5 sm:py-3.5 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] hover:border-b-[#c78ee8] transition-all placeholder:text-[#cbd5e1]"
                />
              </div>
              <div className="flex gap-2 xs:gap-3 mt-5 xs:mt-6">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-3.5 sm:py-4 font-bold text-xs sm:text-base uppercase tracking-wider text-[#64748b] border-[3px] border-[#cbd5e1] rounded-md hover:border-[#a834cb] hover:text-[#a834cb] active:scale-[0.98] transition-all"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 py-3.5 sm:py-4 font-bold text-xs sm:text-base uppercase tracking-wider text-white rounded-md hover:shadow-xl active:scale-[0.98] transition-all border-b-[3px] border-[#8b2db5]"
                  style={{
                    background:
                      "linear-gradient(to right, #a834cb, #8b5cf6, #6366f1)",
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Security */}
          {step === 3 && (
            <div className="space-y-5 xs:space-y-6">
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      checkPasswordStrength(e.target.value);
                    }}
                    placeholder="Create a strong password"
                    maxLength={16}
                    className="w-full px-3 py-2.5 sm:py-3.5 pr-10 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] transition-all placeholder:text-[#cbd5e1]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#a834cb] transition-colors p-2"
                  >
                    <i
                      className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"} text-xs`}
                    />
                  </button>
                </div>
                {password && (
                  <div className="mt-2">
                    <div className="flex gap-1.5 mb-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="flex-1 h-1.5 rounded-full"
                          style={{
                            background:
                              i <= passwordStrength
                                ? strengthColors[passwordStrength - 1]
                                : "#e2e8f0",
                          }}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-[10px]">
                      <span className="text-[#64748b] font-medium">
                        Strength:
                      </span>
                      <span
                        className="font-bold"
                        style={{
                          color:
                            strengthColors[passwordStrength - 1] || "#64748b",
                        }}
                      >
                        {strengthLabels[passwordStrength - 1] ||
                          "Enter a password"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[9px] xs:text-[11px] sm:text-xs tracking-wider font-bold text-[#a834cb] uppercase mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    maxLength={16}
                    className="w-full px-3 py-2.5 sm:py-3.5 pr-10 text-sm sm:text-base text-[#0f172a] font-medium bg-transparent border-b-[3px] border-[#cbd5e1] outline-none focus:border-b-[#a834cb] transition-all placeholder:text-[#cbd5e1]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#a834cb] transition-colors p-2"
                  >
                    <i
                      className={`fas ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"} text-xs`}
                    />
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-[10px] text-[#ef4444] mt-1 font-bold">
                    Passwords do not match
                  </p>
                )}
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded accent-[#a834cb]"
                />
                <span className="text-xs sm:text-sm text-[#475569] font-medium">
                  I agree to Anzo Trade's{" "}
                  <Link to="#" className="text-[#a834cb] font-bold">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="#" className="text-[#a834cb] font-bold">
                    Privacy Policy
                  </Link>
                </span>
              </label>
              <div className="flex gap-2 xs:gap-3 mt-5 xs:mt-6">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-3.5 sm:py-4 font-bold text-xs sm:text-base uppercase tracking-wider text-[#64748b] border-[3px] border-[#cbd5e1] rounded-md hover:border-[#a834cb] hover:text-[#a834cb] active:scale-[0.98] transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3.5 sm:py-4 font-bold text-xs sm:text-base uppercase tracking-wider text-white rounded-md hover:shadow-xl active:scale-[0.98] transition-all border-b-[3px] border-[#8b2db5]"
                  style={{
                    background:
                      "linear-gradient(to right, #a834cb, #8b5cf6, #6366f1)",
                  }}
                >
                  Create Account
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs sm:text-sm text-[#64748b] font-medium">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-[#a834cb] font-bold hover:text-[#8b5cf6]"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* ─── QUESTIONNAIRE MODAL (70% width) ─── */}
      {showQuestionnaire && (
        <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-[70%] max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-pop-in">
            {/* Header */}
            <div
              className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #a834cb15, #8b5cf615)",
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, #a834cb, #8b5cf6)",
                  }}
                >
                  <i className="fas fa-clipboard-list text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Trading Questionnaire
                  </h2>
                  <p className="text-sm text-gray-500">
                    Question {questionStep + 1} of {QUESTIONS.length}
                  </p>
                </div>
              </div>
              <button
                onClick={handleQuestionnaireSkip}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
              >
                <i className="fas fa-xmark text-xl" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-8 pt-5 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500">
                  Progress
                </span>
                <span className="text-xs font-bold text-gray-900">
                  {Math.round(questionnaireProgress)}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${questionnaireProgress}%`,
                    background: "linear-gradient(90deg, #a834cb, #8b5cf6)",
                  }}
                />
              </div>
              {/* Step Dots */}
              <div className="flex justify-between mt-3 px-2">
                {QUESTIONS.map((q, idx) => (
                  <div
                    key={q.page}
                    className={`w-3 h-3 rounded-full transition-all ${idx < questionStep ? "bg-[#10b981]" : idx === questionStep ? "bg-[#a834cb]" : "bg-gray-200"}`}
                  />
                ))}
              </div>
            </div>

            {/* Question */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="mb-4 flex items-center gap-2">
                <span
                  className="text-xs px-3 py-1 rounded-full font-mono font-extrabold border-2"
                  style={{
                    borderColor: "#a834cb",
                    color: "#a834cb",
                    background: "rgba(168, 52, 203, 0.08)",
                  }}
                >
                  {currentQuestion?.page}
                </span>
                <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                  {currentQuestion?.type === "confirmation"
                    ? "Confirmation"
                    : currentQuestion?.type === "yes_no"
                      ? "Yes / No"
                      : "Select one option"}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                {currentQuestion?.question}
              </h3>
              <div className="space-y-3">
                {currentQuestion?.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() =>
                      setAnswers((prev) => ({
                        ...prev,
                        [currentQuestion.page]: option,
                      }))
                    }
                    className={`w-full text-left px-5 py-4 rounded-xl text-sm transition-all border-2 flex items-center gap-4 ${
                      selectedAnswer === option
                        ? "bg-gradient-to-r from-[#a834cb] to-[#8b5cf6] text-white border-transparent shadow-lg"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-[#c78ee8] hover:bg-white"
                    }`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAnswer === option ? "border-white bg-white/20" : "border-gray-300"}`}
                    >
                      {selectedAnswer === option && (
                        <i className="fas fa-check text-xs text-white" />
                      )}
                    </div>
                    <span className="font-medium">{option}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-8 py-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              {questionStep > 0 && (
                <button
                  onClick={() => setQuestionStep((prev) => prev - 1)}
                  className="flex-1 py-3 rounded-lg text-sm font-bold text-gray-600 border-2 border-gray-200 hover:border-[#a834cb] hover:text-[#a834cb] transition-all bg-white"
                >
                  <i className="fas fa-arrow-left mr-2" /> Back
                </button>
              )}
              <button
                onClick={handleQuestionnaireSkip}
                className="flex-1 py-3 rounded-lg text-sm font-bold text-gray-500 hover:text-gray-700 transition-all"
              >
                Skip for now
              </button>
              <button
                onClick={handleQuestionnaireNext}
                disabled={isSubmittingQuestionnaire}
                className="flex-1 py-3 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{
                  background:
                    questionStep === QUESTIONS.length - 1
                      ? "#10b981"
                      : "linear-gradient(to right, #a834cb, #8b5cf6)",
                }}
              >
                {isSubmittingQuestionnaire ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    Saving...
                  </>
                ) : questionStep === QUESTIONS.length - 1 ? (
                  <>
                    <i className="fas fa-check" /> Submit
                  </>
                ) : (
                  <>
                    Next <i className="fas fa-arrow-right" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── KYC MODAL (70% width) ─── */}
      {showKyc && (
        <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-[70%] max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-pop-in">
            {/* Header */}
            <div
              className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #a834cb15, #8b5cf615)",
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, #a834cb, #8b5cf6)",
                  }}
                >
                  <i className="fas fa-id-card text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    KYC Verification
                  </h2>
                  <p className="text-sm text-gray-500">
                    Upload required documents for verification
                  </p>
                </div>
              </div>
              <button
                onClick={handleKycSkip}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
              >
                <i className="fas fa-xmark text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="text-sm text-gray-600 mb-6 flex items-center gap-2">
                <i className="fas fa-info-circle text-[#a834cb]" />
                Upload required documents for KYC verification. Accepted: JPG,
                PNG, PDF (max 10MB). You can skip this for now.
              </div>

              {/* 2x2 Grid of KYC Boxes */}
              <div className="grid grid-cols-2 gap-4">
                {kycDocuments.map((doc) => {
                  const isUploaded = uploadStatus[doc.type] === "uploaded";
                  return (
                    <div
                      key={doc.type}
                      className="rounded-2xl p-6 transition-all border-2 hover:shadow-lg"
                      style={{
                        borderColor: isUploaded ? "#10b981" : "#e5e7eb",
                        background: isUploaded ? "#f0fdf4" : "white",
                      }}
                    >
                      {/* Icon */}
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                        style={{
                          background: doc.bg,
                          border: `2px solid ${doc.color}`,
                        }}
                      >
                        <i
                          className={`fas ${doc.icon} text-xl`}
                          style={{ color: doc.color }}
                        />
                      </div>

                      {/* Title */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-extrabold text-gray-900 text-base">
                          {doc.name}
                        </h3>
                        {doc.required && (
                          <span className="text-red-500 font-bold">*</span>
                        )}
                      </div>

                      {/* Description */}
                      <p className="text-xs text-gray-500 mb-4">{doc.desc}</p>

                      {/* Status */}
                      <div className="mb-4">
                        {isUploaded ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#10b981]">
                            <i className="fas fa-check-circle" /> Uploaded
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400">
                            <i className="fas fa-cloud-upload-alt" /> Not
                            uploaded
                          </span>
                        )}
                      </div>

                      {/* Upload Button */}
                      <input
                        type="file"
                        id={`signup-upload-${doc.type}`}
                        accept=".jpg,.jpeg,.png,.pdf"
                        className="hidden"
                        onChange={(e) =>
                          handleKycFileUpload(doc.type, e.target.files[0])
                        }
                      />
                      <button
                        onClick={() =>
                          document
                            .getElementById(`signup-upload-${doc.type}`)
                            .click()
                        }
                        disabled={isUploading}
                        className="w-full py-3 rounded-lg text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        style={{
                          background: isUploaded ? "#10b981" : doc.color,
                          color: "white",
                          borderBottom:
                            "3px solid " + (isUploaded ? "#047857" : doc.color),
                        }}
                      >
                        {isUploaded ? (
                          <>
                            <i className="fas fa-sync-alt text-xs" /> Re-upload
                          </>
                        ) : (
                          <>
                            <i className="fas fa-upload text-xs" /> Upload
                            Document
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-8 py-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              <button
                onClick={handleKycSkip}
                className="flex-1 py-3 rounded-lg text-sm font-bold text-gray-500 hover:text-gray-700 transition-all"
              >
                Skip for now
              </button>
              <button
                onClick={handleKycSubmit}
                disabled={isSubmittingKyc}
                className="flex-1 py-3 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(to right, #a834cb, #8b5cf6)",
                }}
              >
                {isSubmittingKyc ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane" /> Submit & Go to Terminal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pop-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-pop-in { animation: pop-in 0.25s ease-out; }
        input::placeholder { font-weight: 400; }
        button:active { transform: scale(0.98); }
      `}</style>
    </div>
  );
};

export default SignUp;
